import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { Badge, Button, Field, Input } from '@aeris/ui'

import {
  ApiError,
  fetchChannelCredentials,
  saveChannelApplication,
  startChannelConnect,
  syncChannelStock,
} from '../lib/api.ts'

/**
 * Connecting a Mercado Livre account (F032).
 *
 * Two steps that are genuinely two, and the screen has to say so or people will
 * think the first one failed:
 *
 *   1. **The application** — the App ID and Secret Key from the shop's own
 *      Mercado Livre developer account. Typed once.
 *   2. **The account** — an authorisation the seller grants by logging in at
 *      Mercado Livre. This is the step that produces tokens, and it is the one
 *      that needs the Aeris Cloud to be published on HTTPS.
 *
 * ## The secret is write-only here
 *
 * The field starts empty even when a secret is stored, and the status endpoint
 * cannot return one — `ChannelCredentialStatus` has no field for it. So this
 * component is structurally unable to show a secret back, which is stronger
 * than remembering not to.
 *
 * Leaving it blank on a re-save would be worse than useless, so the button says
 * what it will do: saving requires both fields, every time.
 */

export type ChannelConnectionProps = {
  channelId: string
  channelName: string
  canWrite: boolean
}

export const ChannelConnection = ({
  channelId,
  channelName,
  canWrite,
}: ChannelConnectionProps): ReactNode => {
  const queryClient = useQueryClient()
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const credentials = useQuery({
    queryKey: ['channel-credentials', channelId],
    queryFn: async ({ signal }) => {
      try {
        return await fetchChannelCredentials(channelId, signal)
      } catch (cause) {
        // 404 means "no application configured yet", which is the ordinary
        // starting state rather than a failure worth a red box.
        if (cause instanceof ApiError && cause.status === 404) return null
        throw cause
      }
    },
  })

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['channel-credentials', channelId],
    })
  }

  const save = useMutation({
    mutationFn: async () => {
      await saveChannelApplication(channelId, {
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        webhookSecret:
          webhookSecret.trim() === '' ? null : webhookSecret.trim(),
      })
    },
    onSuccess: async () => {
      setError(null)
      setNotice('Aplicação salva. Agora conecte a conta.')
      // Cleared immediately: a secret sitting in a form field is a secret in a
      // screenshot, a browser autofill store and anybody walking past.
      setClientSecret('')
      setWebhookSecret('')
      await refresh()
    },
    onError: (cause: unknown) => {
      setNotice(null)
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível salvar.'
      )
    },
  })

  const connect = useMutation({
    mutationFn: async () => await startChannelConnect(channelId),
    onSuccess: (result) => {
      setError(null)
      // Opened rather than redirected: this is a fetch from a React screen, and
      // a 302 would be followed by the fetch instead of by the person.
      window.open(result.authorizationUrl, '_blank', 'noopener')
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível iniciar a autorização.'
      )
    },
  })

  const sync = useMutation({
    mutationFn: async () => await syncChannelStock(channelId),
    onSuccess: () => {
      setError(null)
      /*
       * "Enfileirada", e não "atualizados N anúncios" (F109).
       *
       * A publicação saiu da requisição: contar aqui exigiria segurar o
       * navegador durante uma chamada ao Mercado Livre por anúncio. A frase
       * diz o que de fato aconteceu e para onde olhar — a tabela de anúncios
       * mostra, por linha, quando cada um foi publicado e o que deu errado.
       */
      setNotice(
        'Sincronização enfileirada. O resultado aparece na lista de anúncios, por anúncio.'
      )
    },
    onError: (cause: unknown) => {
      setNotice(null)
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível sincronizar.'
      )
    },
  })

  const status = credentials.data ?? null

  return (
    <section className="flex flex-col gap-3 rounded-(--radius-md) border border-(--border-default) p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-(length:--text-sm) font-medium text-(--text-primary)">
          Conexão com o Mercado Livre
        </h3>
        {status === null ? (
          <Badge tone="neutral">Sem aplicação</Badge>
        ) : status.connected ? (
          <Badge tone="success">
            Conectado
            {status.externalNickname === null
              ? ''
              : ` — ${status.externalNickname}`}
          </Badge>
        ) : (
          <Badge tone="neutral">Aplicação salva, conta não conectada</Badge>
        )}
      </header>

      {/*
       * Desde a F122 a aplicação é da Aeris (uma só, registrada uma vez), e o
       * lojista não vê chave nenhuma: clica em "Conectar conta", autoriza no
       * Mercado Livre e volta. Os campos abaixo só aparecem para a loja que
       * tem aplicação PRÓPRIA — a exceção — ou quando a instalação não
       * configurou a da plataforma, e aí a frase diz de quem é o problema.
       */}
      {status?.applicationSource === 'platform' ? (
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Clique em <strong>Conectar conta</strong>: você vai para o Mercado
          Livre, autoriza a Aeris One e volta. Não é preciso criar aplicação nem
          copiar chave nenhuma.
        </p>
      ) : status?.applicationSource === 'missing' ? (
        <p
          role="alert"
          className="text-(length:--text-xs) text-(--text-secondary)"
        >
          A aplicação do Mercado Livre não está configurada nesta instalação do
          Aeris. Fale com quem opera a plataforma — não há nada a fazer nesta
          tela.
        </p>
      ) : (
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Esta loja usa uma aplicação própria. O App ID e a Secret Key saem
          dela, em developers.mercadolivre.com.br. A Secret Key é guardada
          cifrada e nunca volta para esta tela.
        </p>
      )}

      {canWrite && status?.applicationSource === 'tenant' ? (
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="App ID">
            {(field) => (
              <Input
                {...field}
                value={clientId}
                placeholder={status.clientId ?? ''}
                onChange={(event) => {
                  setClientId(event.target.value)
                }}
              />
            )}
          </Field>
          <Field label="Secret Key">
            {(field) => (
              <Input
                {...field}
                type="password"
                value={clientSecret}
                autoComplete="off"
                onChange={(event) => {
                  setClientSecret(event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Segredo das notificações"
            hint="Opcional, e é o que permite verificar que a notificação veio deles."
          >
            {(field) => (
              <Input
                {...field}
                type="password"
                value={webhookSecret}
                autoComplete="off"
                onChange={(event) => {
                  setWebhookSecret(event.target.value)
                }}
              />
            )}
          </Field>
        </div>
      ) : null}

      {notice === null ? null : (
        <p className="text-(length:--text-xs) text-(--text-secondary)">
          {notice}
        </p>
      )}
      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}
      {status?.lastError == null ? null : (
        <p className="text-(length:--text-xs) text-(--danger)">
          Último erro do canal: {status.lastError}
        </p>
      )}

      {canWrite ? (
        <div className="flex flex-wrap gap-2">
          {status?.applicationSource === 'tenant' ? (
            <Button
              variant="secondary"
              disabled={
                save.isPending ||
                clientId.trim() === '' ||
                clientSecret.trim() === ''
              }
              onClick={() => {
                save.mutate()
              }}
            >
              Salvar aplicação
            </Button>
          ) : null}
          <Button
            disabled={
              status === null ||
              connect.isPending ||
              status.applicationSource === 'missing'
            }
            onClick={() => {
              connect.mutate()
            }}
          >
            {status?.connected === true ? 'Reconectar conta' : 'Conectar conta'}
          </Button>
          <Button
            variant="ghost"
            disabled={status?.connected !== true || sync.isPending}
            aria-label={`Sincronizar estoque de ${channelName}`}
            onClick={() => {
              sync.mutate()
            }}
          >
            Sincronizar estoque
          </Button>
        </div>
      ) : null}
    </section>
  )
}
